import { useNativeFilesystem } from '/src/native/useNativeFilesystem.js';

/**
 * FilesystemAdapter - Adapts useNativeFilesystem to persist plugin contract
 *
 * This adapter translates the persist plugin's contract (get, set, remove, clear)
 * to the Capacitor Filesystem API provided by useNativeFilesystem composable.
 *
 * Storage structure:
 * /Data/
 *   └── store-data/
 *       ├── store_faces.json
 *       ├── store_employees.json
 *       └── ...
 *
 * Platform behavior:
 * - Native (iOS/Android): Actual device filesystem - unlimited storage
 * - Web (browser): IndexedDB fallback - limited storage (~50 MB - 1 GB)
 *
 * @returns {Object} Persist plugin storage adapter
 */
export function createFilesystemAdapter () {
	const { readFile, writeFile, deleteFile, mkdir, readdir, Directory, Encoding } = useNativeFilesystem();

	const STORAGE_DIR = 'store-data';
	const FILE_PREFIX = 'store_';

	/**
     * Get filename for a given key
     */
	function getFilePath (key) {
		return `${STORAGE_DIR}/${FILE_PREFIX}${key}.json`;
	}

	/**
     * Ensure storage directory exists
     */
	async function ensureDirectory () {
		try {
			await mkdir({
				path: STORAGE_DIR,
				directory: Directory.Data,
				recursive: true,
			});
		} catch (error) {
			// Silently ignore if directory already exists
			const errorMsg = error.message?.toLowerCase() || '';
			const isAlreadyExists = errorMsg.includes('exist') || errorMsg.includes('already');

			if (!isAlreadyExists) {
				// Only log if it's a real error (not "already exists")
				console.error('[filesystem-adapter] Failed to create directory:', error);
				throw error;
			}
			// Directory exists - that's fine, continue silently
		}
	}

	return {
		/**
         * Get a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to retrieve
         * @returns {Promise<{value: string}>} Object with value property
         * @throws {Error} If key not found
         */
		async get ({ key }) {
			try {
				const { data } = await readFile({
					path: getFilePath(key),
					directory: Directory.Data,
					encoding: Encoding.UTF8,
				});

				return { value: data };
			} catch (error) {
				// File doesn't exist
				if (error.message?.includes('does not exist') || error.message?.includes('not found')) {
					throw new Error(`Key "${key}" not found in filesystem`);
				}

				console.error(`[filesystem-adapter] Failed to read ${key}:`, error);
				throw error;
			}
		},

		/**
         * Set a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to set
         * @param {string} params.value - The value to store (already serialized)
         * @returns {Promise<void>}
         */
		async set ({ key, value }) {
			try {
				await ensureDirectory();

				await writeFile({
					path: getFilePath(key),
					data: value,
					directory: Directory.Data,
					encoding: Encoding.UTF8,
				});

				console.log(`✅ [filesystem-adapter] Saved ${key}, size: ${(value.length / 1024).toFixed(2)} KB`);
			} catch (error) {
				console.error(`❌ [filesystem-adapter] Failed to save ${key}:`, error);
				throw error;
			}
		},

		/**
         * Remove a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to remove
         * @returns {Promise<void>}
         */
		async remove ({ key }) {
			try {
				await deleteFile({
					path: getFilePath(key),
					directory: Directory.Data,
				});

				console.log(`🗑️ [filesystem-adapter] Removed ${key}`);
			} catch (error) {
				// File doesn't exist, that's fine
				if (error.message?.includes('does not exist') || error.message?.includes('not found')) {
					return;
				}

				console.error(`❌ [filesystem-adapter] Failed to remove ${key}:`, error);
				throw error;
			}
		},

		/**
         * Clear all values (persist plugin contract)
         * @returns {Promise<void>}
         */
		async clear () {
			try {
				// Read all files in storage directory
				const { files } = await readdir({
					path: STORAGE_DIR,
					directory: Directory.Data,
				});

				// Delete all files with our prefix
				await Promise.all(
					files
						.filter(file => file.name?.startsWith(FILE_PREFIX))
						.map(file =>
							deleteFile({
								path: `${STORAGE_DIR}/${file.name}`,
								directory: Directory.Data,
							}),
						),
				);

				console.log(`🗑️ [filesystem-adapter] Cleared ${files.length} files`);
			} catch (error) {
				// Directory doesn't exist, that's fine
				if (error.message?.includes('does not exist') || error.message?.includes('not found')) {
					return;
				}

				console.error('[filesystem-adapter] Failed to clear:', error);
				throw error;
			}
		},
	};
}
